import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ActiveRoleProvider } from "@/hooks/useActiveRole";
import { ActivePatientProvider } from "@/hooks/useActivePatient";
import { FeatureVisibilityProvider } from "@/hooks/useFeatureVisibility";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { AppLayout } from "@/components/layout/AppLayout";

// Login is kept eager — it's the landing route for unauthenticated users.
import Login from "./pages/Login";
const Signup = lazy(() => import("./pages/Signup"));

// Everything else is lazily loaded to keep the initial JS bundle small.
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Patients = lazy(() => import("./pages/Patients"));
const EnterMedications = lazy(() => import("./pages/EnterMedications"));
const ManageCarers = lazy(() => import("./pages/ManageCarers"));
const History = lazy(() => import("./pages/History"));
const Appointments = lazy(() => import("./pages/Appointments"));
const CareSettings = lazy(() => import("./pages/CareSettings"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Admin = lazy(() => import("./pages/Admin"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const ApproveCarer = lazy(() => import("./pages/ApproveCarer"));
const AlertAdmin = lazy(() => import("./pages/AlertAdmin"));
const EnterAllergies = lazy(() => import("./pages/EnterAllergies"));
const Children = lazy(() => import("./pages/Children"));
const ChildVaccinations = lazy(() => import("./pages/ChildVaccinations"));
const ChildAllergies = lazy(() => import("./pages/ChildAllergies"));
const ChildCoParents = lazy(() => import("./pages/ChildCoParents"));
const AcceptCoParent = lazy(() => import("./pages/AcceptCoParent"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Emergency = lazy(() => import("./pages/Emergency"));
const MyAccessRequests = lazy(() => import("./pages/MyAccessRequests"));
const CarerOnboarding = lazy(() => import("./pages/CarerOnboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh for 30s and cached for 5min so navigating between
      // menu items uses cached data instead of waiting for a refetch.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Warm up commonly-used route chunks once the app is idle so the first
// menu click doesn't pay the JS download/parse cost.
function PrefetchRoutes() {
  useEffect(() => {
    const w = window as any;
    const idle = (cb: () => void) =>
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(cb, { timeout: 2000 })
        : w.setTimeout(cb, 1500);
    idle(() => {
      import("./pages/Dashboard");
      import("./pages/EnterMedications");
      import("./pages/History");
      import("./pages/SettingsPage");
      import("./pages/Appointments");
    });
  }, []);
  return null;
}

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

function App() { return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ActiveRoleProvider>
      <ActivePatientProvider>
      <FeatureVisibilityProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PrefetchRoutes />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<AppLayout />}>
              {/* Public auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

              {/* Onboarding */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/patients" element={<ProtectedRoute><Patients /></ProtectedRoute>} />
              <Route path="/enter-medications" element={<ProtectedRoute><EnterMedications /></ProtectedRoute>} />
              <Route path="/enter-allergies" element={<ProtectedRoute><EnterAllergies /></ProtectedRoute>} />
              <Route path="/children" element={<ProtectedRoute><Children /></ProtectedRoute>} />
              <Route path="/children/:childId/vaccinations" element={<ProtectedRoute><ChildVaccinations /></ProtectedRoute>} />
              <Route path="/children/:childId/allergies" element={<ProtectedRoute><ChildAllergies /></ProtectedRoute>} />
              <Route path="/children/:childId/co-parents" element={<ProtectedRoute><ChildCoParents /></ProtectedRoute>} />
              <Route path="/accept-co-parent" element={<AcceptCoParent />} />
              <Route path="/emergency" element={<Emergency />} />
              <Route path="/carer-onboarding" element={<ProtectedRoute><CarerOnboarding /></ProtectedRoute>} />
              <Route path="/access-requests" element={<ProtectedRoute><MyAccessRequests /></ProtectedRoute>} />
              <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
              <Route path="/diary" element={<Navigate to="/appointments" replace />} />
              <Route path="/settings/care" element={<ProtectedRoute><CareSettings /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
              <Route path="/alert-admin" element={<ProtectedRoute><AlertAdmin /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/settings/profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
              <Route path="/settings/carers" element={<ProtectedRoute><ManageCarers /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="/approve-carer" element={<ApproveCarer />} />
              <Route path="/terms" element={<ProtectedRoute><Terms /></ProtectedRoute>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </FeatureVisibilityProvider>
      </ActivePatientProvider>
      </ActiveRoleProvider>
    </AuthProvider>
  </QueryClientProvider>
); }

export default App;
